import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Avatar,
  Divider,
  Tabs,
  Tab,
  Radio,
  RadioGroup,
  FormControlLabel,
  InputAdornment,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EventNoteIcon from "@mui/icons-material/EventNote";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import BeachAccessIcon from "@mui/icons-material/BeachAccess";
import WorkIcon from "@mui/icons-material/Work";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const LEAVE_TYPES = [
  { value: "casual", label: "Casual", color: "#3B82F6", icon: "🏖️" },
  { value: "sick", label: "Sick", color: "#EF4444", icon: "🤒" },
  { value: "earned", label: "Earned", color: "#10B981", icon: "⭐" },
  { value: "maternity", label: "Maternity", color: "#EC4899", icon: "👶" },
  { value: "paternity", label: "Paternity", color: "#8B5CF6", icon: "👨‍👶" },
  { value: "unpaid", label: "Unpaid", color: "#6B7280", icon: "📋" },
];

const STATUS_STYLES = {
  pending: {
    label: "Pending",
    bg: "#FEF3C715",
    color: "#D97706",
    border: "#FCD34D40",
  },
  approved: {
    label: "Approved",
    bg: "#D1FAE515",
    color: "#059669",
    border: "#6EE7B740",
  },
  rejected: {
    label: "Rejected",
    bg: "#FEE2E215",
    color: "#DC2626",
    border: "#FCA5A540",
  },
};

const lt = (v) => LEAVE_TYPES.find((t) => t.value === v) || LEAVE_TYPES[0];

const fmtDate = (d) => {
  if (!d) return "—";
  const p = d.split("-");
  const m = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return `${parseInt(p[2])} ${m[parseInt(p[1]) - 1]} ${p[0]}`;
};

const fmtDateRange = (from, to) =>
  from === to ? fmtDate(from) : `${fmtDate(from)} – ${fmtDate(to)}`;

export default function LeavePage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const [tab, setTab] = useState(0);
  const [leaves, setLeaves] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(false);
  const [rejectDialog, setRejectDialog] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [form, setForm] = useState({
    leave_type: "casual",
    from_date: "",
    to_date: "",
    day_type: "full",
    half_day_session: "first",
    reason: "",
  });

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [lRes, sRes] = await Promise.all([
        axios.get(`${API_URL}/leaves`, { headers }),
        axios.get(`${API_URL}/leaves/summary`, { headers }),
      ]);
      setLeaves(lRes.data);
      setSummary(sRes.data);
    } catch {
      setError("Failed to load");
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    setError("");
    try {
      const payload = {
        ...form,
        half_day: form.day_type === "half",
        half_day_session:
          form.day_type === "half" ? form.half_day_session : null,
      };
      await axios.post(`${API_URL}/leaves`, payload, { headers });
      setSuccess("Leave applied! Status: Pending ⏳");
      setOpenForm(false);
      setForm({
        leave_type: "casual",
        from_date: "",
        to_date: "",
        day_type: "full",
        half_day_session: "first",
        reason: "",
      });
      load();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to apply");
    }
  };

  const handleApprove = async (id) => {
    try {
      await axios.put(`${API_URL}/leaves/${id}/approve`, {}, { headers });
      setSuccess("Leave approved ✅");
      load();
    } catch {
      setError("Failed to approve");
    }
  };

  const handleReject = async () => {
    try {
      await axios.put(
        `${API_URL}/leaves/${rejectDialog}/reject`,
        { rejection_reason: rejectReason },
        { headers },
      );
      setSuccess("Leave rejected");
      setRejectDialog(null);
      setRejectReason("");
      load();
    } catch {
      setError("Failed to reject");
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm("Cancel this leave request?")) return;
    try {
      await axios.delete(`${API_URL}/leaves/${id}`, { headers });
      setSuccess("Leave cancelled");
      load();
    } catch {
      setError("Cannot cancel this leave");
    }
  };

  const today = new Date().toISOString().split("T")[0];
  const tabFiltered =
    tab === 0
      ? leaves
      : leaves.filter(
          (l) => l.status === ["pending", "approved", "rejected"][tab - 1],
        );
  const counts = {
    pending: leaves.filter((l) => l.status === "pending").length,
    approved: leaves.filter((l) => l.status === "approved").length,
    rejected: leaves.filter((l) => l.status === "rejected").length,
  };

  const summaryCards = [
    {
      label: "Total Used",
      value: summary.total || 0,
      color: "#1E3A5F",
      icon: "📊",
    },
    {
      label: "Casual",
      value: summary.casual || 0,
      color: "#3B82F6",
      icon: "🏖️",
    },
    { label: "Sick", value: summary.sick || 0, color: "#EF4444", icon: "🤒" },
    {
      label: "Earned",
      value: summary.earned || 0,
      color: "#10B981",
      icon: "⭐",
    },
    {
      label: "Unpaid",
      value: summary.unpaid || 0,
      color: "#6B7280",
      icon: "📋",
    },
  ];

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          mb: 3,
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 900,
              letterSpacing: "-0.5px",
              fontSize: { xs: "1.6rem", sm: "2rem" },
            }}
          >
            Leave Management
          </Typography>
          <Typography color="text.secondary" variant="body2" sx={{ mt: 0.3 }}>
            Apply and track leave requests
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenForm(true)}
          sx={{
            borderRadius: 2,
            fontWeight: 700,
            px: { xs: 1.5, sm: 2.5 },
            flexShrink: 0,
            whiteSpace: "nowrap",
            boxShadow: "0 4px 14px rgba(30,58,95,0.25)",
          }}
        >
          <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
            Apply{" "}
          </Box>
          Leave
        </Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {summaryCards.map((s, i) => (
          <Grid
            item
            xs={6}
            sm={4}
            md={2.4}
            key={i}
            sx={{ maxWidth: { xs: "50%" } }}
          >
            <Card
              sx={{
                border: "1px solid",
                borderColor: s.color + "30",
                boxShadow: "none",
                borderRadius: 2.5,
                transition: "all 0.15s",
                "&:hover": {
                  borderColor: s.color,
                  transform: "translateY(-2px)",
                  boxShadow: `0 8px 24px ${s.color}15`,
                },
              }}
            >
              <CardContent
                sx={{ p: 2, pb: "16px !important", textAlign: "center" }}
              >
                <Typography sx={{ fontSize: "1.5rem", mb: 0.3 }}>
                  {s.icon}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 900, color: s.color, lineHeight: 1 }}
                >
                  {s.value}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    fontSize: "0.62rem",
                    letterSpacing: "0.06em",
                    display: "block",
                    mt: 0.3,
                  }}
                >
                  {s.label}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: "text.disabled", fontSize: "0.58rem" }}
                >
                  days this year
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2, borderRadius: 2 }}
          onClose={() => setError("")}
        >
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2, borderRadius: 2 }}
          onClose={() => setSuccess("")}
        >
          {success}
        </Alert>
      )}

      {/* Table Card */}
      <Card
        sx={{
          boxShadow: "none",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 3,
          overflow: "hidden",
        }}
      >
        {/* Tab bar */}
        <Box
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            bgcolor: "background.default",
            px: 1,
          }}
        >
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              "& .MuiTab-root": {
                fontSize: "0.8rem",
                fontWeight: 700,
                minHeight: 48,
              },
            }}
          >
            {[
              { label: "All", count: leaves.length },
              { label: "Pending", count: counts.pending, color: "#D97706" },
              { label: "Approved", count: counts.approved, color: "#059669" },
              { label: "Rejected", count: counts.rejected, color: "#DC2626" },
            ].map((t, i) => (
              <Tab
                key={i}
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                    {t.label}
                    <Chip
                      label={t.count}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: "0.62rem",
                        fontWeight: 800,
                        bgcolor:
                          tab === i
                            ? (t.color || "primary.main") + "20"
                            : "action.hover",
                        color:
                          tab === i
                            ? t.color || "primary.main"
                            : "text.secondary",
                      }}
                    />
                  </Box>
                }
              />
            ))}
          </Tabs>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : tabFiltered.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <EventNoteIcon
              sx={{
                fontSize: 48,
                opacity: 0.1,
                display: "block",
                mx: "auto",
                mb: 1.5,
              }}
            />
            <Typography color="text.secondary" sx={{ fontWeight: 600 }}>
              No leave records found
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow
                  sx={{
                    "& .MuiTableCell-head": {
                      bgcolor: "#F8FAFC",
                      fontWeight: 700,
                      color: "#64748B",
                      fontSize: "0.7rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      py: 1.5,
                      borderBottom: "2px solid",
                      borderColor: "divider",
                    },
                  }}
                >
                  {isAdmin && <TableCell>Employee</TableCell>}
                  <TableCell>Leave Type</TableCell>
                  <TableCell>Date(s)</TableCell>
                  <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>
                    Duration
                  </TableCell>
                  <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
                    Reason
                  </TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>
                    Applied
                  </TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tabFiltered.map((l) => {
                  const ltype = lt(l.leave_type);
                  const st = STATUS_STYLES[l.status];
                  return (
                    <TableRow
                      key={l.id}
                      hover
                      sx={{
                        "&:hover": { bgcolor: "#F8FAFC" },
                        "& .MuiTableCell-root": {
                          py: 1.5,
                          borderColor: "divider",
                        },
                      }}
                    >
                      {isAdmin && (
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Avatar
                              sx={{
                                width: 30,
                                height: 30,
                                bgcolor: "primary.main",
                                fontSize: "0.7rem",
                                fontWeight: 800,
                              }}
                            >
                              {l.employee?.name?.charAt(0)}
                            </Avatar>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600 }}
                            >
                              {l.employee?.name}
                            </Typography>
                          </Box>
                        </TableCell>
                      )}
                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.8,
                          }}
                        >
                          <Typography sx={{ fontSize: "1rem" }}>
                            {ltype.icon}
                          </Typography>
                          <Chip
                            label={ltype.label}
                            size="small"
                            sx={{
                              bgcolor: ltype.color + "15",
                              color: ltype.color,
                              fontWeight: 700,
                              fontSize: "0.65rem",
                              border: `1px solid ${ltype.color}30`,
                            }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {fmtDateRange(l.from_date, l.to_date)}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={{ display: { xs: "none", sm: "table-cell" } }}
                      >
                        <Chip
                          label={
                            l.half_day
                              ? `½ Day (${l.half_day_session === "first" ? "AM" : "PM"})`
                              : `${l.total_days} day${l.total_days > 1 ? "s" : ""}`
                          }
                          size="small"
                          variant="outlined"
                          sx={{ fontWeight: 700, fontSize: "0.65rem" }}
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          maxWidth: 160,
                          display: { xs: "none", md: "table-cell" },
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {l.reason}
                        </Typography>
                        {l.rejection_reason && (
                          <Typography
                            variant="caption"
                            color="error"
                            sx={{ display: "block" }}
                          >
                            ↳ {l.rejection_reason}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={st.label}
                          size="small"
                          sx={{
                            bgcolor: st.bg,
                            color: st.color,
                            fontWeight: 700,
                            fontSize: "0.65rem",
                            border: `1px solid ${st.border}`,
                          }}
                        />
                      </TableCell>
                      <TableCell
                        sx={{ display: { xs: "none", sm: "table-cell" } }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          {fmtDate(l.created_at?.split("T")[0])}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Stack
                          direction="row"
                          spacing={0.5}
                          justifyContent="center"
                        >
                          {isAdmin && l.status === "pending" && (
                            <>
                              <Tooltip title="Approve">
                                <IconButton
                                  size="small"
                                  onClick={() => handleApprove(l.id)}
                                  sx={{
                                    width: 28,
                                    height: 28,
                                    bgcolor: "#D1FAE5",
                                    color: "#059669",
                                    "&:hover": { bgcolor: "#A7F3D0" },
                                  }}
                                >
                                  <CheckIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Reject">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setRejectDialog(l.id);
                                    setRejectReason("");
                                  }}
                                  sx={{
                                    width: 28,
                                    height: 28,
                                    bgcolor: "#FEE2E2",
                                    color: "#DC2626",
                                    "&:hover": { bgcolor: "#FECACA" },
                                  }}
                                >
                                  <CloseIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                          {isAdmin && l.status !== "pending" && (
                            <Typography
                              variant="caption"
                              color="text.disabled"
                              sx={{ fontSize: "0.7rem" }}
                            >
                              by {l.approver?.name || "—"}
                            </Typography>
                          )}
                          {!isAdmin && l.status === "pending" && (
                            <Tooltip title="Cancel request">
                              <IconButton
                                size="small"
                                onClick={() => handleCancel(l.id)}
                                sx={{
                                  width: 28,
                                  height: 28,
                                  bgcolor: "#FEE2E2",
                                  color: "#DC2626",
                                  "&:hover": { bgcolor: "#FECACA" },
                                }}
                              >
                                <DeleteIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {/* Apply Leave Dialog */}
      <Dialog
        open={openForm}
        onClose={() => setOpenForm(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <Box
          sx={{
            height: 4,
            background: "linear-gradient(90deg, #3B82F6, #10B981)",
            borderRadius: "12px 12px 0 0",
          }}
        />
        <DialogTitle sx={{ pt: 3, pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: "#3B82F615",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CalendarMonthIcon sx={{ color: "#3B82F6" }} />
            </Box>
            <Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 800, lineHeight: 1.2 }}
              >
                Apply for Leave
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Status will be Pending until approved
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 2, borderRadius: 2 }}
              onClose={() => setError("")}
            >
              {error}
            </Alert>
          )}
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            {/* Leave type */}
            <Box>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: "text.secondary",
                  textTransform: "uppercase",
                  fontSize: "0.68rem",
                  letterSpacing: "0.08em",
                  display: "block",
                  mb: 1,
                }}
              >
                Leave Type
              </Typography>
              <Grid container spacing={1}>
                {LEAVE_TYPES.map((t) => (
                  <Grid item xs={6} sm={4} key={t.value}>
                    <Box
                      onClick={() => setForm({ ...form, leave_type: t.value })}
                      sx={{
                        p: 1.2,
                        borderRadius: 2,
                        border: "1.5px solid",
                        cursor: "pointer",
                        textAlign: "center",
                        borderColor:
                          form.leave_type === t.value ? t.color : "divider",
                        bgcolor:
                          form.leave_type === t.value
                            ? t.color + "12"
                            : "transparent",
                        transition: "all 0.15s",
                        "&:hover": {
                          borderColor: t.color,
                          bgcolor: t.color + "08",
                        },
                      }}
                    >
                      <Typography sx={{ fontSize: "1.2rem" }}>
                        {t.icon}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          color:
                            form.leave_type === t.value
                              ? t.color
                              : "text.secondary",
                          fontSize: "0.68rem",
                        }}
                      >
                        {t.label}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Dates */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="From Date"
                  type="date"
                  size="small"
                  value={form.from_date}
                  onChange={(e) =>
                    setForm({ ...form, from_date: e.target.value })
                  }
                  inputProps={{ min: today }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="To Date"
                  type="date"
                  size="small"
                  value={form.to_date}
                  onChange={(e) =>
                    setForm({ ...form, to_date: e.target.value })
                  }
                  inputProps={{ min: form.from_date || today }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>

            {/* Duration */}
            <Box
              sx={{
                p: 2,
                bgcolor: "background.default",
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: "text.secondary",
                  textTransform: "uppercase",
                  fontSize: "0.68rem",
                  letterSpacing: "0.08em",
                  display: "block",
                  mb: 1,
                }}
              >
                Duration
              </Typography>
              <RadioGroup
                row
                value={form.day_type}
                onChange={(e) => setForm({ ...form, day_type: e.target.value })}
              >
                <FormControlLabel
                  value="full"
                  control={<Radio size="small" />}
                  label={
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Full Day
                    </Typography>
                  }
                />
                <FormControlLabel
                  value="half"
                  control={<Radio size="small" />}
                  label={
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Half Day
                    </Typography>
                  }
                />
              </RadioGroup>
              {form.day_type === "half" && (
                <RadioGroup
                  row
                  value={form.half_day_session}
                  onChange={(e) =>
                    setForm({ ...form, half_day_session: e.target.value })
                  }
                  sx={{ mt: 1, pl: 1 }}
                >
                  <FormControlLabel
                    value="first"
                    control={<Radio size="small" color="primary" />}
                    label={
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, lineHeight: 1 }}
                        >
                          1st Half
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Morning
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="second"
                    control={<Radio size="small" color="secondary" />}
                    label={
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, lineHeight: 1 }}
                        >
                          2nd Half
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Afternoon
                        </Typography>
                      </Box>
                    }
                    sx={{ ml: 2 }}
                  />
                </RadioGroup>
              )}
            </Box>

            {/* Reason */}
            <TextField
              fullWidth
              required
              multiline
              rows={3}
              label="Reason"
              size="small"
              placeholder="Please provide a reason for your leave..."
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
            />

            {/* Preview */}
            {form.from_date && form.to_date && (
              <Box
                sx={{
                  p: 1.5,
                  bgcolor: "#3B82F608",
                  borderRadius: 2,
                  border: "1px solid #3B82F620",
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <CalendarMonthIcon
                  sx={{ color: "#3B82F6", fontSize: 20, flexShrink: 0 }}
                />
                <Box>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 700, color: "#3B82F6" }}
                  >
                    {fmtDateRange(form.from_date, form.to_date)}
                    {form.day_type === "half"
                      ? ` · Half Day (${form.half_day_session === "first" ? "Morning" : "Afternoon"})`
                      : " · Full Day"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Pending approval
                  </Typography>
                </Box>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setOpenForm(false)}
            sx={{ flex: 1, borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleApply}
            disabled={!form.from_date || !form.to_date || !form.reason}
            sx={{
              flex: 2,
              borderRadius: 2,
              fontWeight: 700,
              background: "linear-gradient(135deg, #3B82F6, #2563EB)",
              boxShadow: "0 4px 14px rgba(59,130,246,0.3)",
            }}
          >
            Submit Leave Request
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog
        open={!!rejectDialog}
        onClose={() => setRejectDialog(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <Box
          sx={{ height: 4, bgcolor: "#EF4444", borderRadius: "12px 12px 0 0" }}
        />
        <DialogTitle sx={{ fontWeight: 800 }}>Reject Leave</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Reason for Rejection"
            size="small"
            placeholder="Provide a reason..."
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setRejectDialog(null)}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<CloseIcon />}
            onClick={handleReject}
            sx={{ borderRadius: 2, fontWeight: 700 }}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
